/**
 * Public NGO promo page at /ngo/{slug}.
 * Fetches active-only via GET /api/adoption-agencies/by-slug/{slug}.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Heart, Globe, MapPin, Mail, Loader2, ExternalLink, Share2, Facebook, Instagram, Twitter } from 'lucide-react';
import SEO from '../components/SEO';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SEC-002 defense-in-depth (Feb 2026): even though the backend now
 * rejects `javascript:`/`data:`/`vbscript:` schemes on the NGO signup
 * form, older records may still have unsafe URLs. Return null for
 * anything that isn't a plain http(s) URL so the render skips it.
 */
function safeExternalUrl(u) {
  if (!u || typeof u !== 'string') return null;
  const trimmed = u.trim();
  if (!/^https?:\/\//i.test(trimmed)) return null;
  return trimmed;
}

export default function NgoPromoPage() {
  const { slug } = useParams();
  const [ngo, setNgo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/adoption-agencies/by-slug/${slug}`);
        setNgo(res.data);
      } catch (err) {
        if (err.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: ngo.name, text: ngo.mission || ngo.description, url });
      } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (notFound || !ngo) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="ngo-promo-not-found">
          <CardContent className="text-center p-8">
            <h1 className="text-2xl font-heading font-bold mb-2">NGO not found</h1>
            <p className="text-muted-foreground mb-6">
              This promo page hasn&apos;t been approved yet, or the URL is incorrect.
            </p>
            <div className="flex gap-2 justify-center">
              <Link to="/"><Button variant="outline">Home</Button></Link>
              <Link to="/ngo/join"><Button className="btn-primary">List your rescue</Button></Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const location = [ngo.city, ngo.state].filter(Boolean).join(', ');
  const websiteUrl = safeExternalUrl(ngo.website);
  const facebookUrl = safeExternalUrl(ngo.facebook_url);
  const instagramUrl = safeExternalUrl(ngo.instagram_url);
  const twitterUrl = safeExternalUrl(ngo.twitter_url);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${ngo.name} — All American Dog Club`}
        description={ngo.mission || ngo.description || `Support ${ngo.name}, a dog rescue organization.`}
        image={ngo.cover_image_url || ngo.logo_url || undefined}
      />

      {/* Hero */}
      <div className="relative">
        <div
          className="h-56 md:h-72 bg-gradient-to-br from-orange-300 to-orange-600 bg-cover bg-center"
          style={ngo.cover_image_url ? { backgroundImage: `url(${ngo.cover_image_url})` } : {}}
        />
        <div className="max-w-4xl mx-auto px-4 -mt-16 relative">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="w-28 h-28 rounded-2xl bg-white shadow-lg border-4 border-white overflow-hidden flex items-center justify-center" data-testid="ngo-logo">
              {ngo.logo_url ? (
                <img src={ngo.logo_url} alt={ngo.name} className="w-full h-full object-cover" />
              ) : (
                <Heart className="w-12 h-12 text-orange-500" />
              )}
            </div>
            <div className="flex-1 min-w-0 pb-2">
              <div className="flex items-center gap-2 flex-wrap">
                {ngo.is_ngo_verified && (
                  <Badge className="bg-green-100 text-green-700 border-green-200">Verified</Badge>
                )}
                {ngo.is_no_kill && (
                  <Badge variant="outline">No-kill</Badge>
                )}
                <Badge variant="outline" className="capitalize">{ngo.agency_type}</Badge>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold mt-1" data-testid="ngo-name">{ngo.name}</h1>
              {location && (
                <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                  <MapPin className="w-4 h-4" /> {location}
                </div>
              )}
            </div>
            <Button variant="outline" onClick={share} data-testid="ngo-share-btn">
              <Share2 className="w-4 h-4 mr-2" /> Share
            </Button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-4 py-10 grid md:grid-cols-3 gap-8">
        {/* Mission + description */}
        <div className="md:col-span-2 space-y-6">
          {ngo.mission && (
            <section>
              <h2 className="text-xl font-heading font-semibold mb-2">Our mission</h2>
              <p className="text-muted-foreground whitespace-pre-line" data-testid="ngo-mission">{ngo.mission}</p>
            </section>
          )}
          {ngo.description && ngo.description !== ngo.mission && (
            <section>
              <h2 className="text-xl font-heading font-semibold mb-2">About us</h2>
              <p className="text-muted-foreground whitespace-pre-line">{ngo.description}</p>
            </section>
          )}
          {ngo.services && ngo.services.length > 0 && (
            <section>
              <h2 className="text-xl font-heading font-semibold mb-2">Services</h2>
              <div className="flex gap-2 flex-wrap">
                {ngo.services.map((s) => (
                  <Badge key={s} variant="outline" className="capitalize">{s.replace(/_/g, ' ')}</Badge>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar: contact & CTAs */}
        <aside className="space-y-4">
          <Card>
            <CardContent className="p-4 space-y-3">
              {websiteUrl && (
                <a
                  href={websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                  data-testid="ngo-website-link"
                >
                  <Globe className="w-4 h-4" /> Visit website <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {ngo.email && (
                <a
                  href={`mailto:${ngo.email}`}
                  className="flex items-center gap-2 text-sm text-primary hover:underline break-all"
                  data-testid="ngo-email-link"
                >
                  <Mail className="w-4 h-4 shrink-0" /> {ngo.email}
                </a>
              )}
              <div className="flex gap-2">
                {facebookUrl && (
                  <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:opacity-70">
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {instagramUrl && (
                  <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:opacity-70">
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
                {twitterUrl && (
                  <a href={twitterUrl} target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:opacity-70">
                    <Twitter className="w-5 h-5" />
                  </a>
                )}
              </div>
            </CardContent>
          </Card>

          {ngo.accepts_donations && (
            <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-200">
              <CardContent className="p-4 text-center">
                <Heart className="w-10 h-10 mx-auto text-orange-500 mb-2" />
                <div className="font-semibold">Support this rescue</div>
                <p className="text-xs text-muted-foreground mb-3">
                  Donations go directly through the NGO&apos;s own website.
                </p>
                {websiteUrl ? (
                  <a href={websiteUrl} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full btn-primary" data-testid="ngo-donate-btn">Visit to donate</Button>
                  </a>
                ) : (
                  <Button className="w-full" variant="outline" disabled>Contact for donations</Button>
                )}
              </CardContent>
            </Card>
          )}

          <div className="text-xs text-muted-foreground text-center">
            Listed for free on All American Dog Club<br />
            <Link to="/ngo/join" className="text-primary hover:underline">List your rescue too →</Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
